import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiClient } from '../lib/api.js';
import { cn, formatBRL } from '../lib/utils.js';

function roasClass(roas: number): string {
  if (roas >= 2) return 'text-emerald-400 font-bold';
  if (roas >= 1) return 'text-amber-300 font-bold';
  if (roas > 0) return 'text-rose-400 font-bold';
  return 'text-slate-500';
}

export function CampanhasMiniTable(): JSX.Element {
  const month = new Date().toISOString().slice(0, 7);
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-campaigns', month],
    queryFn: () => apiClient.trafficOverview(month),
    refetchInterval: 60_000,
  });

  if (isLoading || !data) return <div className="p-4 text-xs text-muted-foreground">Carregando campanhas...</div>;

  const top = [...data.campaigns]
    .sort((a, b) => Number(b.spendCents) - Number(a.spendCents))
    .slice(0, 10);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/30 border-b border-border">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Campanha</th>
            <th className="px-3 py-2 text-right font-medium">Gasto</th>
            <th className="px-3 py-2 text-right font-medium">Criativos</th>
            <th className="px-3 py-2 text-right font-medium">Pedidos</th>
            <th className="px-3 py-2 text-right font-medium">Receita</th>
            <th className="px-3 py-2 text-right font-medium">ROAS</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/40">
          {top.map((c) => {
            const spend = Number(c.spendCents);
            const gross = Number(c.grossCents);
            const roas = spend > 0 ? gross / spend : 0;
            return (
              <tr key={c.campaign} className="hover:bg-muted/30">
                <td className="px-3 py-2">
                  <Link
                    to={`/trafego/campanha/${c.campaign}`}
                    className="text-cyan-300 hover:text-cyan-200"
                    title={c.campaign}
                  >
                    <span className="block max-w-[260px] truncate font-medium">
                      {c.campaignName ?? c.campaign}
                    </span>
                  </Link>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{formatBRL(spend)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{c.creatives}</td>
                <td className="px-3 py-2 text-right tabular-nums">{c.orders}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatBRL(gross)}</td>
                <td className={cn('px-3 py-2 text-right tabular-nums', roasClass(roas))}>
                  {roas > 0 ? `${roas.toFixed(2)}x` : '—'}
                </td>
              </tr>
            );
          })}
          {top.length === 0 && (
            <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">Sem campanhas no mês.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function CriativosMiniTable(): JSX.Element {
  const month = new Date().toISOString().slice(0, 7);
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard-creatives', month],
    queryFn: () => apiClient.trafficOverview(month),
    refetchInterval: 60_000,
  });

  if (isLoading || !data) return <div className="p-4 text-xs text-muted-foreground">Carregando criativos...</div>;

  const top = [...data.creatives]
    .sort((a, b) => Number(b.spendCents) - Number(a.spendCents))
    .slice(0, 15);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/30 border-b border-border">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Campanha</th>
            <th className="px-3 py-2 text-left font-medium">Criativo</th>
            <th className="px-3 py-2 text-right font-medium">Gasto</th>
            <th className="px-3 py-2 text-right font-medium">Pedidos</th>
            <th className="px-3 py-2 text-right font-medium">Receita</th>
            <th className="px-3 py-2 text-right font-medium">ROAS</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/40">
          {top.map((c) => {
            const spend = Number(c.spendCents);
            const gross = Number(c.grossCents);
            const roas = spend > 0 ? gross / spend : 0;
            return (
              <tr key={`${c.campaign}-${c.creative}`} className="hover:bg-muted/30">
                <td className="px-3 py-2">
                  <span className="block text-xs truncate max-w-[180px] text-muted-foreground">
                    {c.campaignName ?? c.campaign}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <span className="block text-xs truncate max-w-[260px]">
                    {c.adName ?? <span className="font-mono text-[10px]">{c.creative}</span>}
                  </span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{formatBRL(spend)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{c.orders}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatBRL(gross)}</td>
                <td className={cn('px-3 py-2 text-right tabular-nums', roasClass(roas))}>
                  {roas > 0 ? `${roas.toFixed(2)}x` : '—'}
                </td>
              </tr>
            );
          })}
          {top.length === 0 && (
            <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">Sem criativos.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
